import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LinkifiedText } from "@/components/LinkifiedText";
import { PostImageGallery } from "@/components/PostImageGallery";
import { useSlowLoadingFallback } from "@/hooks/useSlowLoadingFallback";
import { supabase } from "@/integrations/supabase/client";
import { buildFeedTitle, COLUMNAS_FEED_ITEM, isVideoUrl, mergeFeedDescriptions, type FeedItemRow } from "@/lib/contentModels";
import { galleryImageUrl, thumbnailImageUrl } from "@/lib/imageCdn";
import { atributosDeCarga } from "@/lib/imagePriority";
import { recuerdaPrimeraFoto } from "@/lib/primeraFoto";
import { traerTodasLasFilas } from "@/lib/traerTodasLasFilas";
import { devLogQueryPerf, readLocalQueryCache, readLocalUiState, writeLocalQueryCache, writeLocalUiState } from "@/lib/localQueryCache";
import { usePersistWindowScroll } from "@/lib/scrollRestore";

type ViewMode = "grid" | "scroll";
const FEED_UI_KEY = "feed-ui-state";
const FEED_GRID_SCROLL_KEY = "feed-grid-scroll-y";
const FEED_GRID_POST_KEY = "feed-grid-post-id";
const FEED_GRID_POST_OFFSET_KEY = "feed-grid-post-offset-y";
const PAGE_SIZE = 16;
/**
 * Filas de la consulta rapida, la que desbloquea el primer pintado. Se pintan
 * PAGE_SIZE posts y un post son unas ocho filas y media de media, asi que con
 * 300 entran unos treinta y cinco: margen de sobra sin traerse el historico.
 */
const TOPE_INICIAL = 300;

/*
 * La vista NO se recuerda entre visitas, a proposito. Antes si, y el efecto
 * era que pulsar "Conciertos" te metia en la rejilla porque fue lo ultimo que
 * tocaste hace dias, y desde ahi tenias que pasar a scroll. El feed abre en
 * scroll, que es como se lee; la rejilla se pide, y mientras la uses viaja en
 * la URL (`?view=grid`), asi que el boton de atras y los enlaces compartidos
 * siguen llevando a donde tienen que llevar.
 */
type FeedUiState = {
  searchQuery: string;
  visibleCount: number;
};

const Feed = () => {
  usePersistWindowScroll("/feed");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUi = readLocalUiState<FeedUiState>(FEED_UI_KEY);
  const initialViewParam = searchParams.get("view");
  const initialQueryParam = searchParams.get("q");
  const initialPostParam = searchParams.get("post");
  const [viewMode, setViewMode] = useState<ViewMode>(
    initialViewParam === "grid" || initialViewParam === "scroll" ? initialViewParam : "scroll"
  );
  const [searchQuery, setSearchQuery] = useState(initialQueryParam ?? initialUi?.searchQuery ?? "");
  const [visibleCount, setVisibleCount] = useState(() => {
    const raw = initialUi?.visibleCount ?? PAGE_SIZE;
    return Number.isFinite(raw) ? Math.max(PAGE_SIZE, raw) : PAGE_SIZE;
  });
  const [isPositioningPost, setIsPositioningPost] = useState(
    initialViewParam === "scroll" && Boolean(initialPostParam)
  );
  const [isRestoringGrid, setIsRestoringGrid] = useState(false);
  const [pendingScrollPostId, setPendingScrollPostId] = useState<string | null>(null);
  const [pendingGridRestorePostId, setPendingGridRestorePostId] = useState<string | null>(null);
  const handledPostParamRef = useRef<string | null>(null);
  const hasHydratedUiRef = useRef(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const skipNextUrlWriteRef = useRef(false);
  const previousViewModeRef = useRef<ViewMode>(viewMode);
  const previousSearchQueryRef = useRef(searchQuery);
  const skipGridRestoreRef = useRef(false);
  const searchParamsSnapshot = searchParams.toString();

  useEffect(() => {
    hasHydratedUiRef.current = true;
  }, []);

  useEffect(() => {
    const viewParam = searchParams.get("view");
    const qParam = searchParams.get("q");
    const nextViewMode =
      viewParam === "grid" || viewParam === "scroll" ? viewParam : null;
    const shouldSyncView = nextViewMode !== null && nextViewMode !== viewMode;
    const shouldSyncQuery = (qParam ?? "") !== searchQuery;

    if (shouldSyncView || shouldSyncQuery) {
      skipNextUrlWriteRef.current = true;
    }

    if (shouldSyncView && nextViewMode) {
      setViewMode(nextViewMode);
    }
    if (shouldSyncQuery) {
      setSearchQuery(qParam ?? "");
    }
  }, [searchParamsSnapshot]);

  /*
   * Dos consultas sobre la misma tabla, y no una.
   *
   * La lista completa son 1173 filas en dos peticiones: medido en produccion,
   * 863 → 1493 ms antes de poder pintar una sola tarjeta. Y hasta que no
   * llega no se sabe la URL de la primera foto, asi que de nada sirve
   * marcarla como prioritaria: no hay nada que pedir todavia.
   *
   * La rapida trae solo las primeras filas, con el MISMO filtro y el MISMO
   * orden, asi que sus posts son exactamente los primeros de la lista buena.
   * Se pinta con ella y la completa la sustituye cuando llega. El feed la
   * necesita entera para buscar sobre todo el historico y para "ver mas".
   */
  const rapida = useQuery({
    queryKey: ["feed-items-primeros"],
    queryFn: async () => {
      const startedAt = performance.now();
      // `as never` y no `as any`: los tipos generados no cubren esta tabla,
      // y asi no se añade un `any` mas de los que ya arrastra el fichero.
      const { data, error: e } = await supabase
        .from("feed_items" as never)
        .select(COLUMNAS_FEED_ITEM)
        .eq("published", true)
        .order("fecha_utc", { ascending: false })
        .order("id", { ascending: false })
        .limit(TOPE_INICIAL);
      if (e) throw e;
      const rows = (data ?? []) as FeedItemRow[];
      devLogQueryPerf("feed-items-primeros", startedAt, rows.length);
      return rows;
    },
    initialData: () => readLocalQueryCache<FeedItemRow[]>("cache:feed-items-primeros"),
    initialDataUpdatedAt: 0,
    placeholderData: (previousData) => previousData,
    retry: 1,
  });

  const completa = useQuery({
    queryKey: ["feed-items"],
    queryFn: async () => {
      const startedAt = performance.now();
      // Paginado: sin esto PostgREST cortaba en 1000 filas de las 1173
      // publicadas. El desempate por id hace el orden total, que es lo que
      // necesita la paginacion para no repetir ni saltarse filas.
      const rows = await traerTodasLasFilas<FeedItemRow>((desde, hasta) =>
        supabase
          .from("feed_items" as never)
          .select(COLUMNAS_FEED_ITEM)
          .eq("published", true)
          .order("fecha_utc", { ascending: false })
          .order("id", { ascending: false })
          .range(desde, hasta),
      );
      devLogQueryPerf("feed-items", startedAt, rows.length);
      return rows;
    },
    placeholderData: (previousData) => previousData,
    retry: 1,
    /*
     * Espera a la rapida en vez de salir con ella. Lanzandolas a la vez,
     * medido en produccion, la rapida tardaba 459 ms en lugar de los ~220
     * que tarda sola: competian por la misma base y se estorbaban, con lo
     * que lo de "pintar antes" no servia de nada.
     *
     * Encadenadas, la lista completa llega algo mas tarde, pero eso pasa de
     * fondo y el visitante ya esta viendo el feed.
     */
    enabled: rapida.isSuccess,
  });

  // La completa manda en cuanto tiene algo; hasta entonces se pinta la rapida.
  const posts = completa.data?.length ? completa.data : rapida.data;
  // Solo falla de verdad si fallan las dos: con una basta para enseñar algo.
  const isError = rapida.isError && completa.isError;
  const error = completa.error ?? rapida.error;

  /*
   * Solo se guarda la lista corta. La completa ocupaba 916 KB en
   * sessionStorage y no aportaba nada que esta no cubra: sirve para pintar al
   * instante al recargar la pestaña, y para eso bastan las primeras filas.
   * Entre las tres caches de esta misma tabla habia 1,4 MB guardados.
   */
  useEffect(() => {
    if (rapida.data) writeLocalQueryCache("cache:feed-items-primeros", rapida.data);
  }, [rapida.data]);

  const grouped = useMemo(() => {
    const map = new Map<string, FeedItemRow[]>();
    for (const row of posts ?? []) {
      const list = map.get(row.post_id) ?? [];
      list.push(row);
      map.set(row.post_id, list);
    }

    const groups: Array<{
      post_id: string;
      created_at: string;
      descripcion: string;
      urls: string[];
      link: string;
      representativeRowId: number;
    }> = [];

    for (const rows of map.values()) {
      rows.sort((a, b) => (a.item_index ?? 0) - (b.item_index ?? 0));
      let descripcion = "";
      for (const row of rows) {
        descripcion = mergeFeedDescriptions(descripcion, row.descripcion);
      }
      groups.push({
        post_id: rows[0].post_id,
        created_at: rows[0].fecha_utc ?? new Date().toISOString(),
        descripcion: descripcion.trim(),
        urls: rows.map((row) => row.archivo).filter((url): url is string => Boolean(url)),
        link: rows.find((row) => row.link?.trim())?.link?.trim() ?? "",
        representativeRowId: rows[0].id,
      });
    }

    groups.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return b.representativeRowId - a.representativeRowId;
    });

    return groups;
  }, [posts]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredList = useMemo(
    () =>
      grouped.filter((post) => {
        if (!normalizedQuery) return true;
        const candidate = `${post.descripcion} ${post.post_id} ${post.link}`.toLowerCase();
        return candidate.includes(normalizedQuery);
      }),
    [grouped, normalizedQuery]
  );

  const sortedList = filteredList;

  // Solo "cargando" mientras no haya nada que enseñar: si la rapida ya ha
  // pintado, que la completa siga trabajando detras no es asunto del visitante.
  const showLoading = sortedList.length === 0 && (rapida.isPending || rapida.isFetching || completa.isFetching);
  const slowLoad = useSlowLoadingFallback(showLoading, isError);
  const safeVisibleCount = Math.min(Math.max(visibleCount, PAGE_SIZE), Math.max(sortedList.length, PAGE_SIZE));
  const visibleList = sortedList.slice(0, safeVisibleCount);
  const hasMore = visibleList.length < sortedList.length;

  // Apuntar cual es la primera foto para que la proxima visita la pida antes
  // de que exista el bundle (el porque, en src/lib/primeraFoto.ts). Con una
  // busqueda activa no, que la lista no es la de siempre.
  const primeraUrl = visibleList[0]?.urls[0];
  useEffect(() => {
    if (searchQuery.trim() || !primeraUrl || isVideoUrl(primeraUrl)) return;
    recuerdaPrimeraFoto(viewMode === "grid" ? thumbnailImageUrl(primeraUrl) : galleryImageUrl(primeraUrl));
  }, [primeraUrl, searchQuery, viewMode]);

  const emptyMessage = isError
    ? `No se ha podido cargar el feed. ${error instanceof Error ? error.message : ""}`.trim()
    : searchQuery.trim()
      ? "No hay coincidencias con la búsqueda."
      : "Pronto habrá fotos por aquí.";

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    if (previousSearchQueryRef.current === searchQuery) return;
    previousSearchQueryRef.current = searchQuery;
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery]);

  useEffect(() => {
    if (sortedList.length === 0) return;
    setVisibleCount((prev) => Math.min(Math.max(prev, PAGE_SIZE), sortedList.length));
  }, [sortedList.length]);

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    writeLocalUiState<FeedUiState>(FEED_UI_KEY, {
      searchQuery,
      visibleCount: Math.max(PAGE_SIZE, safeVisibleCount),
    });
  }, [searchQuery, safeVisibleCount]);

  useEffect(() => {
    if (!hasHydratedUiRef.current) return;
    if (skipNextUrlWriteRef.current) {
      skipNextUrlWriteRef.current = false;
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("view", viewMode);
    if (searchQuery.trim()) next.set("q", searchQuery);
    else next.delete("q");
    if (next.toString() === searchParamsSnapshot) return;
    setSearchParams(next, { replace: true });
  }, [viewMode, searchQuery, searchParamsSnapshot, searchParams, setSearchParams]);

  useLayoutEffect(() => {
    const targetPostId = searchParams.get("post");
    if (!targetPostId) {
      setIsPositioningPost(false);
      return;
    }
    if (sortedList.length === 0) return;
    if (handledPostParamRef.current === targetPostId) return;
    const idx = sortedList.findIndex((p) => p.post_id === targetPostId);
    if (idx < 0) return;
    handledPostParamRef.current = targetPostId;
    setIsPositioningPost(true);
    setVisibleCount((prev) => Math.max(prev, Math.ceil((idx + 1) / PAGE_SIZE) * PAGE_SIZE));
    setPendingScrollPostId(targetPostId);
  }, [searchParams, sortedList]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const previousView = previousViewModeRef.current;
    if (previousView === "scroll" && viewMode === "grid") {
      if (skipGridRestoreRef.current) {
        skipGridRestoreRef.current = false;
        setPendingGridRestorePostId(null);
        setIsRestoringGrid(false);
        const root = document.documentElement;
        const previousBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = "auto";
        window.scrollTo(0, 0);
        root.style.scrollBehavior = previousBehavior;
        previousViewModeRef.current = viewMode;
        return;
      }
      setIsRestoringGrid(true);
      const postId = window.sessionStorage.getItem(FEED_GRID_POST_KEY);
      if (postId) {
        setPendingGridRestorePostId(postId);
      } else {
        const raw = window.sessionStorage.getItem(FEED_GRID_SCROLL_KEY);
        const y = raw ? Number(raw) : 0;
        const root = document.documentElement;
        const previousBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = "auto";
        window.scrollTo(0, Number.isNaN(y) ? 0 : Math.max(0, y));
        root.style.scrollBehavior = previousBehavior;
        setIsRestoringGrid(false);
      }
    }
    previousViewModeRef.current = viewMode;
  }, [viewMode]);

  useLayoutEffect(() => {
    if (!pendingGridRestorePostId) return;
    if (typeof window === "undefined") return;
    const targetEl = document.getElementById(`feed-grid-post-${pendingGridRestorePostId}`);
    if (!targetEl) return;

    let frameId = 0;
    let lastAbsoluteTop = -1;
    let lastHeight = -1;
    let stablePasses = 0;
    const rawOffset = window.sessionStorage.getItem(FEED_GRID_POST_OFFSET_KEY);
    const rawY = window.sessionStorage.getItem(FEED_GRID_SCROLL_KEY);
    const targetOffset = rawOffset ? Number(rawOffset) : 0;
    const fallbackY = rawY ? Number(rawY) : 0;

    const jumpToGridPost = () => {
      const rect = targetEl.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const root = document.documentElement;
      const previousBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      if (Number.isFinite(targetOffset) && targetOffset > 0) {
        window.scrollTo(0, Math.max(0, absoluteTop - targetOffset));
      } else {
        window.scrollTo(0, Number.isNaN(fallbackY) ? 0 : Math.max(0, fallbackY));
      }
      root.style.scrollBehavior = previousBehavior;
    };

    const checkAndJump = () => {
      const rect = targetEl.getBoundingClientRect();
      const absoluteTop = Math.round(rect.top + window.scrollY);
      const height = Math.round(rect.height);
      if (absoluteTop === lastAbsoluteTop && height === lastHeight) stablePasses += 1;
      else stablePasses = 0;
      lastAbsoluteTop = absoluteTop;
      lastHeight = height;
      jumpToGridPost();
      if (stablePasses >= 2) {
        setPendingGridRestorePostId(null);
        setIsRestoringGrid(false);
        return;
      }
      frameId = window.requestAnimationFrame(checkAndJump);
    };

    const resizeObserver = new ResizeObserver(() => {
      stablePasses = 0;
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(checkAndJump);
    });

    resizeObserver.observe(targetEl);
    frameId = window.requestAnimationFrame(checkAndJump);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [pendingGridRestorePostId, safeVisibleCount]);

  useLayoutEffect(() => {
    if (!pendingScrollPostId) return;
    const targetEl = document.getElementById(`feed-post-${pendingScrollPostId}`);
    if (!targetEl) return;
    if (typeof window === "undefined") return;

    let frameId = 0;
    let lastHeight = -1;
    let lastAbsoluteTop = -1;
    let stablePasses = 0;

    const jumpToPost = () => {
      const rect = targetEl.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const headerOffset = 88;
      const targetTop = absoluteTop - (headerOffset + 20);
      const root = document.documentElement;
      const previousBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      window.scrollTo(0, Math.max(0, targetTop));
      requestAnimationFrame(() => {
        root.style.scrollBehavior = previousBehavior;
      });
    };

    const checkAndJump = () => {
      const rect = targetEl.getBoundingClientRect();
      const height = Math.round(rect.height);
      const absoluteTop = Math.round(rect.top + window.scrollY);
      if (height === lastHeight && absoluteTop === lastAbsoluteTop) stablePasses += 1;
      else stablePasses = 0;
      lastHeight = height;
      lastAbsoluteTop = absoluteTop;
      jumpToPost();
      if (stablePasses >= 2) {
        setPendingScrollPostId(null);
        setIsPositioningPost(false);
        return;
      }
      frameId = window.requestAnimationFrame(checkAndJump);
    };

    const resizeObserver = new ResizeObserver(() => {
      stablePasses = 0;
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(checkAndJump);
    });

    resizeObserver.observe(targetEl);
    frameId = window.requestAnimationFrame(checkAndJump);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [pendingScrollPostId, safeVisibleCount, viewMode]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return;
    const node = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        /*
         * Mientras la rejilla se esta colocando, la pagina todavia mide lo que
         * la pantalla y el centinela cae dentro: saltaba dos veces seguidas y
         * se plantaba en el tope sin que nadie hubiera tocado la rueda.
         * Medido al entrar en /feed?view=grid y no tocar nada: 40 tarjetas y
         * 2,2 MB de fotos para una pantalla de 900 px. Si no hay nada que
         * desplazar, no hay nadie pidiendo mas.
         */
        if (document.documentElement.scrollHeight <= window.innerHeight + 200) return;
        setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, sortedList.length));
      },
      { rootMargin: "320px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, safeVisibleCount, sortedList.length]);

  return (
    <div className="mx-auto max-w-[80rem] px-5 py-10 md:px-9">
      <div className="mb-10">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
          <div>
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">Conciertos</h1>
            <p className="mt-1 text-sm text-muted-foreground">Instantes de música en vivo</p>
          </div>
          <div className="flex w-full flex-col gap-2 md:w-auto md:min-w-0 md:items-end">
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar conciertos..."
                className="h-11 w-full min-w-0 rounded-lg border border-[#13151c]/24 bg-background px-4 text-sm text-foreground outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 sm:w-[20rem] sm:max-w-full"
                aria-label="Buscar conciertos"
              />
              <div className="grid w-full shrink-0 grid-cols-2 gap-1 rounded-lg border border-border bg-muted/40 p-0.5 sm:w-[7.5rem]">
                {(["grid", "scroll"] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      if (mode === viewMode) return;
                      if (mode === "grid") skipGridRestoreRef.current = true;
                      const root = document.documentElement;
                      const previousBehavior = root.style.scrollBehavior;
                      root.style.scrollBehavior = "auto";
                      window.scrollTo(0, 0);
                      root.style.scrollBehavior = previousBehavior;
                      setViewMode(mode);
                    }}
                    className={`flex h-11 items-center justify-center rounded-md px-2 ${
                      viewMode === mode ? "bg-foreground text-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                    }`}
                    aria-label={mode === "grid" ? "Vista grid" : "Vista scroll"}
                  >
                    {mode === "grid" ? (
                      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden>
                        <rect x="2.5" y="2.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                        <rect x="11.5" y="2.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                        <rect x="2.5" y="11.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                        <rect x="11.5" y="11.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden>
                        <rect x="3" y="3.2" width="14" height="3.2" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                        <rect x="3" y="8.4" width="14" height="3.2" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                        <rect x="3" y="13.6" width="14" height="3.2" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {sortedList.length > 0 ? (
        viewMode === "grid" ? (
          <div className={`grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 ${isRestoringGrid ? "invisible" : "visible"}`}>
            {visibleList.map((post, idx) => {
              const urls = post.urls;
              const title = buildFeedTitle({
                id: 0,
                post_id: post.post_id,
                shortcode: null,
                post_type: null,
                item_index: 0,
                media_type: null,
                fecha_utc: post.created_at,
                descripcion: post.descripcion,
                link: null,
                archivo: urls[0] ?? null,
                published: true,
              });
              // En la vista cuadrícula el texto va recortado a 3 líneas, así que aplastamos los
              // saltos de párrafo a un espacio para no malgastar líneas en huecos en blanco.
              const gridDescription = (post.descripcion?.trim() || title).replace(/\s*\n\s*/g, " ");
              return (
                <button
                  key={post.post_id}
                  id={`feed-grid-post-${post.post_id}`}
                  type="button"
                  className="group relative overflow-hidden rounded-lg"
                  onClick={(e) => {
                    if (typeof window !== "undefined") {
                      window.sessionStorage.setItem(FEED_GRID_SCROLL_KEY, String(window.scrollY));
                      window.sessionStorage.setItem(FEED_GRID_POST_KEY, post.post_id);
                      window.sessionStorage.setItem(FEED_GRID_POST_OFFSET_KEY, String(e.currentTarget.getBoundingClientRect().top));
                    }
                    const next = new URLSearchParams(searchParams);
                    next.set("view", "scroll");
                    next.set("post", post.post_id);
                    setSearchParams(next);
                  }}
                  aria-label={`Abrir concierto ${title} en modo scroll`}
                >
                  {urls[0] ? (
                    <>
                      {isVideoUrl(urls[0]) ? (
                        <video
                          src={urls[0]}
                          className="block h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                          muted
                          playsInline
                          loop
                          autoPlay
                          preload="metadata"
                        />
                      ) : (
                        // Hasta 4 columnas: la primera fila se ve entera al
                        // entrar, asi que no espera. Alta, solo la primera.
                        <img
                          src={thumbnailImageUrl(urls[0])}
                          alt={title}
                          {...atributosDeCarga(idx < 4, idx === 0)}
                          className="block h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                        />
                      )}
                      {urls.length > 1 && (
                        <span className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center text-white" aria-label="Varias fotos o videos">
                          <svg viewBox="0 0 24 24" className="h-[17px] w-[17px]" fill="none" aria-hidden>
                            <rect x="8.8" y="6.8" width="9.4" height="9.4" rx="2" stroke="currentColor" strokeOpacity="0.92" strokeWidth="1.9" />
                            <rect x="5.8" y="3.8" width="9.4" height="9.4" rx="2" stroke="currentColor" strokeOpacity="0.75" strokeWidth="1.9" />
                          </svg>
                        </span>
                      )}
                    </>
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">Sin imagen</div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/72 to-transparent p-3 pt-10">
                    <p className="font-mono text-[12px] leading-relaxed text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.85)] line-clamp-3 text-left [text-align-last:left]">
                      {gridDescription}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className={`mx-auto flex max-w-5xl flex-col gap-12 lg:gap-16 ${isPositioningPost ? "invisible" : "visible"}`}>
            {visibleList.map((post, idx) => {
              const urls = post.urls;
              const title = buildFeedTitle({
                id: 0,
                post_id: post.post_id,
                shortcode: null,
                post_type: null,
                item_index: 0,
                media_type: null,
                fecha_utc: post.created_at,
                descripcion: post.descripcion,
                link: null,
                archivo: urls[0] ?? null,
                published: true,
              });
              const caption = post.descripcion?.trim() ?? "";
              const headline = caption || title;
              return (
                <article
                  key={post.post_id}
                  id={`feed-post-${post.post_id}`}
                  data-post-id={post.post_id}
                  className="grid gap-7 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,440px)] lg:items-center lg:gap-12"
                >
                  <div className="min-w-0 lg:justify-self-stretch">
                    <div className="mx-auto w-fit max-w-full overflow-hidden rounded-xl">
                      <PostImageGallery
                        urls={urls}
                        alt={headline}
                        layout="carousel"
                        maxHeight={680}
                        imgClassName="object-contain"
                        prioritaria={idx === 0}
                      />
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-col justify-center gap-4 lg:py-2">
                    <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                      {new Date(post.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                    <LinkifiedText
                      text={headline}
                      className="font-display text-base leading-snug text-foreground md:text-lg lg:text-xl"
                    />
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      {post.link ? (
                        <a
                          href={post.link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-primary underline-offset-4 transition-colors hover:underline"
                        >
                          Abrir en Instagram {"->"}
                        </a>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )
      ) : showLoading && !slowLoad ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] animate-pulse rounded-lg bg-muted" />)}
        </div>
      ) : (
        <p className="py-16 text-center text-muted-foreground">{emptyMessage}</p>
      )}

      {sortedList.length > 0 && hasMore && (
        <div ref={loadMoreRef} className="py-8 text-center text-xs text-muted-foreground">
          Cargando más conciertos...
        </div>
      )}
    </div>
  );
};

export default Feed;
